import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../../../../shared/ui/button/button.component';

/**
 * Aviso modal BLOQUEANTE y reutilizable. Nace en un único sitio (no se controla
 * con un servicio global): el padre lo gobierna con el input `open` y reacciona
 * a `acknowledge`. El copy es parametrizable (`title`, `body`, mascota, labels)
 * para servir a distintos avisos del checkout (p. ej. apartados).
 *
 * Es estrictamente bloqueante: no se cierra con Escape ni con clic en el
 * backdrop, y el botón de continuar sólo se habilita cuando el usuario marca el
 * checkbox de confirmación de lectura. El patrón de focus-trap, guardas SSR y
 * bloqueo de scroll del body sigue a `stock-alert-modal`.
 */
@Component({
  selector: 'app-blocking-notice-modal',
  imports: [ButtonComponent],
  templateUrl: './blocking-notice-modal.component.html',
  styleUrl: './blocking-notice-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockingNoticeModalComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = input<boolean>(false);
  readonly title = input<string>('');
  readonly body = input<string>('');
  /** Bloque de énfasis opcional, diferenciado del cuerpo (borde + fondo sutil). */
  readonly emphasis = input<string>('');
  readonly mascotSrc = input<string>('/images/logo/info.png');
  /** Lado de la mascota respecto al texto. `right` la coloca «leyendo» el aviso. */
  readonly mascotPosition = input<'left' | 'right'>('left');
  /** Dimensiones intrínsecas del PNG (evitan CLS/deformación). Default = info.png. */
  readonly mascotWidth = input<number>(481);
  readonly mascotHeight = input<number>(640);
  readonly ackLabel = input<string>('He leído y entiendo');
  readonly ctaLabel = input<string>('Continuar');
  readonly acknowledge = output<void>();

  /** Estado local del checkbox de confirmación. Habilita el botón. */
  protected readonly acknowledged = signal(false);

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor() {
    // Reset del checkbox + guardar/restaurar foco + bloqueo de scroll, según `open`.
    effect(() => {
      const isOpen = this.open();
      if (!isPlatformBrowser(this.platformId)) return;
      document.body.style.overflow = isOpen ? 'hidden' : '';

      if (isOpen) {
        // Cada apertura exige una re-confirmación consciente.
        this.acknowledged.set(false);
        this.previouslyFocusedElement =
          (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const root = this.dialogRef()?.nativeElement;
          if (root) this.getFocusableElements(root)[0]?.focus();
        });
      } else if (this.previouslyFocusedElement) {
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) el.focus({ preventScroll: true });
        });
      }
    });

    // Focus trap BLOQUEANTE: traga Escape y cicla Tab/Shift+Tab dentro del modal.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.open()) return;

          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          if (e.key !== 'Tab') return;

          const modal = this.dialogRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) {
            e.preventDefault();
            return;
          }

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onToggleAck(event: Event): void {
    this.acknowledged.set((event.target as HTMLInputElement).checked);
  }

  protected onAcknowledge(): void {
    // Guarda: el botón sólo procede si el usuario confirmó la lectura.
    if (!this.acknowledged()) return;
    this.acknowledge.emit();
  }

  /** Devuelve los elementos enfocables dentro del modal en orden DOM. */
  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
